<template>
  <div>
    <!-- Loading State -->
    <div v-if="isLoading" class="tw-space-y-4">
      <base-card v-for="i in 2" :key="i">
        <div class="tw-my-2">
          <div class="tw-mb-6 tw-flex tw-justify-between tw-items-center">
            <div class="tw-flex tw-gap-4">
              <base-skeleton-loader class="tw-h-4 tw-w-40" />
              <base-skeleton-loader class="tw-h-4 tw-w-32" />
            </div>
            <base-skeleton-loader class="tw-h-4 tw-w-36" />
          </div>
          <div class="tw-pl-4 tw-space-y-4">
            <div v-for="j in 4" :key="j" class="tw-border-b tw-py-2">
              <div class="tw-flex tw-items-center tw-gap-3">
                <base-skeleton-loader class="tw-h-6 tw-w-6 tw-rounded-full" />
                <base-skeleton-loader class="tw-h-6 tw-w-48" />
                <base-skeleton-loader class="tw-h-4 tw-w-32 tw-ml-auto" />
              </div>
            </div>
          </div>
        </div>
      </base-card>
    </div>

    <!-- Empty State -->
    <div v-else-if="orderTrackings.length === 0">
      <base-card>
        <p class="">No orders to display.</p>
      </base-card>
    </div>

    <!-- Orders List -->
    <div v-else>
      <div v-for="order in orderTrackings" :key="order.businessPrescriptionOrderId" class="tw-mb-10">
        <base-card>
          <div class="tw-my-2">
            <div class="tw-mb-6 tw-text-sm tw-flex tw-justify-between tw-items-center">
              <div class="tw-flex tw-gap-4">
                <p>
                  Pharmacy Order ID: <span class="tw-text-gray-500">{{ order.pharmacyOrderId }}</span>
                </p>
                <p>
                  Pharmacy:
                  <span class="tw-text-primary tw-font-medium">{{
                    order.pharmacy && (order.pharmacy.toLowerCase() == "lifefile" || order.pharmacy.toLowerCase() == "innovation") ? "Innovation" : order.pharmacy
                  }}</span>
                </p>
              </div>
              <div class="tw-flex tw-items-center tw-gap-3">
                <button
                  v-if="!hasActivityName(order.businessPrescriptionOrderId)"
                  type="button"
                  class="tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-primary tw-border tw-border-primary tw-rounded-md hover:tw-bg-primary hover:tw-text-white tw-transition disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  :disabled="isActivityNameLoading(order.businessPrescriptionOrderId)"
                  @click="showActivityName(order)"
                >
                  {{ isActivityNameLoading(order.businessPrescriptionOrderId) ? "Loading..." : "Show Activity Name" }}
                </button>
                <p v-else>
                  Activity Name: <span class="tw-text-primary tw-font-medium">{{ getActivityName(order.businessPrescriptionOrderId) }}</span>
                </p>
                <p v-if="getActivityNameError(order.businessPrescriptionOrderId)" class="tw-text-xs tw-text-red-500">
                  {{ getActivityNameError(order.businessPrescriptionOrderId) }}
                </p>
              </div>
            </div>
            <div class="tw-flex">
              <div class="tw-flex-1 tw-pl-4">
                <div v-for="(status, idx) in getDisplayStatuses()" :key="status" class="tw-relative tw-border-b tw-py-2">
                  <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <div
                      :class="[
                        'tw-h-6 tw-w-6 tw-rounded-full tw-border-2 tw-flex tw-items-center tw-justify-center',
                        idx <= getCurrentStatusIndex(order) ? 'tw-bg-green-500 tw-border-green-500' : 'tw-bg-white tw-border-gray-300',
                      ]"
                    >
                      <svg v-if="idx <= getCurrentStatusIndex(order)" class="tw-h-4 tw-w-4 tw-text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div class="tw-flex-1">
                      <div
                        :class="[
                          'tw-inline-block tw-px-2 tw-py-1.5 tw-rounded-full tw-text-lg',
                          idx === getCurrentStatusIndex(order)
                            ? 'tw-text-green-700 tw-font-semibold'
                            : idx < getCurrentStatusIndex(order)
                            ? 'tw-text-gray-700 tw-font-light'
                            : 'tw-text-gray-400 tw-font-light',
                        ]"
                      >
                        {{ formatTypeAsStatus(status) }}
                      </div>
                      <div v-if="idx === getCurrentStatusIndex(order)" class="tw-text-gray-700 tw-text-sm tw-px-2">
                        {{ getStatusDescription(status) }}
                      </div>
                    </div>
                    <span v-if="findLogByStatus(order, status)" class="tw-text-gray-500 tw-font-light tw-text-xs tw-min-w-[150px] tw-text-right tw-whitespace-nowrap">
                      {{ formatDate(findLogByStatus(order, status).insertedDate) }}
                    </span>
                  </div>
                </div>
                <div v-if="getLastLogWithTracking(order) && getLastLogWithTracking(order).trackingUrl" class="tw-mt-4">
                  <a
                    :href="getLastLogWithTracking(order).trackingUrl"
                    target="_blank"
                    class="tw-inline-block tw-text-white tw-bg-skyblue hover:tw-bg-sky-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-blue tw-px-6 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition"
                    >Track Your Package</a
                  >
                </div>
              </div>
              <!-- end RIGHT -->
            </div>
          </div>
        </base-card>
      </div>
    </div>
  </div>
</template>

<style>
.tw-bg-skyblue {
  background-color: #2196f3;
}
</style>

<script>
import moment from "moment";

export default {
  name: "BusinessMemberTrackingList",
  props: {
    userId: String,
    businessId: String,
  },
  data() {
    return {
      isLoading: false,
      orderTrackings: [],
      activityNames: {},
      activityNameLoading: {},
      activityNameErrors: {},
      statuses: ["new", "inpharmacy", "readytopack", "readytoship", "shipped"],
      stepHeight: 50,
    };
  },
  mounted() {
    this.getOrderTrackingLogs();
  },
  methods: {
    async getOrderTrackingLogs() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/members/${this.userId}/orders/tracking`);
        this.orderTrackings = response.data;
      } finally {
        this.isLoading = false;
      }
    },
    getActivityName(orderId) {
      return this.activityNames[orderId];
    },
    hasActivityName(orderId) {
      return Object.prototype.hasOwnProperty.call(this.activityNames, orderId);
    },
    isActivityNameLoading(orderId) {
      return Boolean(this.activityNameLoading[orderId]);
    },
    getActivityNameError(orderId) {
      return this.activityNameErrors[orderId];
    },
    async showActivityName(order) {
      const orderId = order.businessPrescriptionOrderId;
      if (!orderId || this.hasActivityName(orderId) || this.isActivityNameLoading(orderId)) return;

      this.$set(this.activityNameLoading, orderId, true);
      this.$delete(this.activityNameErrors, orderId);

      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/members/${this.userId}/orders/${orderId}/activity-name`);
        this.$set(this.activityNames, orderId, response.data.activityName || "Unknown");
      } catch (error) {
        this.$set(this.activityNameErrors, orderId, "Unable to load activity name.");
      } finally {
        this.$delete(this.activityNameLoading, orderId);
      }
    },
    getDisplayStatuses() {
      return this.statuses.filter((s) => s.toLowerCase() !== "new" && s.toLowerCase() !== "order_placed");
    },
    formatDate(dt) {
      if (!dt) return "-";
      return moment(dt).format("M/D/YY h:mm A");
    },
    formatTypeAsStatus(type) {
      if (!type) return "";
      switch (type.toLowerCase()) {
        case "inpharmacy":
          return "Sent to Pharmacy";
        case "readytopack":
          return "In Pharmacy Processing";
        case "readytoship":
          return "Packaging & Final Quality Check";
        case "shipped":
          return "Shipping Label Created";
        default:
          if (type.toLowerCase().startsWith("order_")) {
            const readable = type.replace(/_/g, " ").replace("order ", "");
            return readable.charAt(0).toUpperCase() + readable.slice(1);
          }
          return type.charAt(0).toUpperCase() + type.slice(1);
      }
    },
    getStatusDescription(status) {
      switch (status.toLowerCase()) {
        case "inpharmacy":
          return "Your prescription has been sent to our pharmacy partner for processing.";
        case "readytopack":
          return "The pharmacy is reviewing your prescription and preparing your medication.";
        case "readytoship":
          return "Your medication has been prepared and is undergoing a final quality check by a pharmacist. Once reviewed, it will be packaged and made ready for pickup by the carrier.";
        case "shipped":
          return "A shipping label has been created, and your tracking link is now available. The carrier may not have picked up the package yet. Please use the tracking link below for the most up-to-date delivery details.";
        default:
          return "";
      }
    },
    getCurrentStatusIndex(order) {
      if (!order || !order.logs || !order.logs.length) return -1;
      // Filter out 'new' or order_placed status
      const filteredLogs = order.logs.filter((log) => log.status && (log.status.toLowerCase() !== "new" && log.status.toLowerCase() !== "order_placed"));
      if (!filteredLogs.length) return -1;
      const lastLogStatus = filteredLogs[filteredLogs.length - 1].status.toLowerCase();
      const displayStatuses = this.getDisplayStatuses();
      const idx = displayStatuses.findIndex((s) => s.toLowerCase() === lastLogStatus);
      return idx;
    },
    findLogByStatus(order, status) {
      if (!order || !order.logs) return undefined;
      // Filter out 'new' status
      const filteredLogs = order.logs.filter((log) => log.status && (log.status.toLowerCase() !== "new" && log.status.toLowerCase() !== "order_placed"));
      return filteredLogs.find((x) => x.status && x.status.toLowerCase() === status.toLowerCase());
    },
    getLastLogWithTracking(order) {
      if (!order.logs || !order.logs.length) return null;
      for (let i = order.logs.length - 1; i >= 0; i--) {
        if (order.logs[i].trackingUrl) {
          return order.logs[i];
        }
      }
      return null;
    },
  },
};
</script>
